import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../store/authSlice';
import api from '../services/api';
import Navigation from '../components/Navigation';
import { useTheme } from '../context/ThemeContext';
import {
  Users,
  Wallet,
  Zap,
  TrendingUp,
  Gift,
  Landmark,
  User,
  LogOut,
  IndianRupee,
  Sun,
  Moon,
} from 'lucide-react';

function Dashboard() {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dark, toggle } = useTheme();
  const [wallet, setWallet] = useState(0);
  const [summary, setSummary] = useState(null);

  const partners = [
    'Hypto',
    'Paymitra',
    'Opencash',
    'Zetech IT',
    'Growsper',
    'Cover Insurance',
    'Onmart',
    'Skill Trust',
    'Recharge Panda',
  ];
  const sliderList = [...partners, ...partners];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletRes, incomeRes] = await Promise.all([
        api.get('/Wallet/balance'),
        api.get('/Income/summary'),
      ]);
      setWallet(walletRes.data.balance || 0);
      setSummary(incomeRes.data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const menus = [
    { title: 'Add Member', path: '/add-member', icon: Users, color: 'bg-blue-500' },
    { title: 'My Team', path: '/add-member', icon: Users, color: 'bg-indigo-500' },
    { title: 'Recharge', path: '/recharge', icon: Zap, color: 'bg-green-500' },
    { title: 'Apply Loan', path: '/apply-loan', icon: Landmark, color: 'bg-purple-500' },
    { title: 'Income', path: '/income', icon: TrendingUp, color: 'bg-orange-500' },
    { title: 'Gift', path: '/income', icon: Gift, color: 'bg-pink-500' },
    { title: 'Wallet', path: '/wallet', icon: Wallet, color: 'bg-teal-500' },
    { title: 'Profile', path: '/profile', icon: User, color: 'bg-gray-600' },
  ];

  const page = dark ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900';
  const card = dark ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow';
  const muted = dark ? 'text-gray-400' : 'text-gray-500';
  const title = dark ? 'text-gray-200' : 'text-gray-700';
  const partnerCard = dark
    ? 'bg-gray-800 border border-gray-700'
    : 'bg-white shadow border border-gray-100';

  return (
    <div className={`min-h-screen pb-24 transition-colors ${page}`}>
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white px-4 pt-6 pb-8 rounded-b-3xl">
        <div className="flex justify-between items-start mb-5">
          <div>
            <p className="text-blue-200 text-sm">Welcome back</p>
            <h2 className="text-xl font-bold">{user?.fullName || 'User'}</h2>
            <p className="text-blue-200 text-xs mt-1">
              {user?.referralCode ? `Code: ${user.referralCode}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggle}
              className="bg-white/20 p-2 rounded-full"
              title={dark ? 'Light mode' : 'Dark mode'}
            >
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 bg-white/20 px-3 py-1.5 rounded-full text-sm"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>

        {/* Wallet Card */}
        <div className="bg-white/15 backdrop-blur rounded-2xl p-4">
          <div className="flex items-center gap-2 text-blue-100 text-sm mb-1">
            <IndianRupee size={16} />
            <span>Wallet Balance</span>
          </div>
          <p className="text-3xl font-bold">₹{wallet.toLocaleString()}</p>
          <div className="flex gap-2 mt-3">
            <Link
              to="/wallet"
              className="flex-1 bg-white text-blue-700 text-center text-sm font-semibold py-2 rounded-xl"
            >
              Withdraw
            </Link>
            <Link
              to="/income"
              className="flex-1 bg-blue-500 text-white text-center text-sm font-semibold py-2 rounded-xl"
            >
              View Income
            </Link>
          </div>
        </div>
      </div>

      {/* Income Summary */}
      {summary && (
        <div className={`mx-4 -mt-4 rounded-2xl p-4 ${card}`}>
          <h3 className={`font-semibold text-sm mb-3 ${title}`}>Income Summary</h3>
          <div className="grid grid-cols-2 gap-2">
            <div className={`rounded-xl p-3 ${dark ? 'bg-blue-900/40' : 'bg-blue-50'}`}>
              <p className={`text-xs ${muted}`}>Level 1</p>
              <p className="font-bold text-blue-500">₹{summary.level1 || 0}</p>
            </div>
            <div className={`rounded-xl p-3 ${dark ? 'bg-green-900/40' : 'bg-green-50'}`}>
              <p className={`text-xs ${muted}`}>Level 2</p>
              <p className="font-bold text-green-500">₹{summary.level2 || 0}</p>
            </div>
            <div className={`rounded-xl p-3 ${dark ? 'bg-pink-900/40' : 'bg-pink-50'}`}>
              <p className={`text-xs ${muted}`}>Gift</p>
              <p className="font-bold text-pink-500">₹{summary.gift || 0}</p>
            </div>
            <div className={`rounded-xl p-3 ${dark ? 'bg-orange-900/40' : 'bg-orange-50'}`}>
              <p className={`text-xs ${muted}`}>Passive</p>
              <p className="font-bold text-orange-500">₹{summary.passive || 0}</p>
            </div>
          </div>
          <p className={`text-center font-bold mt-3 text-sm ${title}`}>
            Total: ₹{(summary.totalIncome || 0).toLocaleString()}
          </p>
        </div>
      )}

      {/* Menu Grid */}
      <div className="mx-4 mt-5">
        <h3 className={`font-semibold text-sm mb-3 ${title}`}>Quick Actions</h3>
        <div className="grid grid-cols-4 gap-3">
          {menus.map((menu) => {
            const Icon = menu.icon;
            return (
              <Link
                key={menu.title}
                to={menu.path}
                className="flex flex-col items-center gap-1.5"
              >
                <div
                  className={`${menu.color} w-14 h-14 rounded-2xl flex items-center justify-center shadow-md`}
                >
                  <Icon size={24} className="text-white" />
                </div>
                <span
                  className={`text-xs text-center font-medium leading-tight ${
                    dark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  {menu.title}
                </span>
              </Link>
            );
          })}

          {/* Gold Price */}
          <a
            href="https://goldprice.org/live-gold-price.html"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-1.5"
          >
            <div className="bg-yellow-500 w-14 h-14 rounded-2xl flex items-center justify-center shadow-md">
              <span className="text-2xl">🥇</span>
            </div>
            <span
              className={`text-xs text-center font-medium leading-tight ${
                dark ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              Gold Price
            </span>
          </a>
        </div>
      </div>

      {/* Status */}
      <div
        className={`mx-4 mt-5 rounded-2xl p-4 flex justify-between items-center ${card}`}
      >
        <div>
          <p className={`text-xs ${muted}`}>Account Status</p>
          <p
            className={`font-bold text-sm ${
              user?.isActive ? 'text-green-500' : 'text-red-500'
            }`}
          >
            {user?.isActive ? '✅ Active' : '⏳ Pending Payment'}
          </p>
        </div>
        {!user?.isActive && (
          <Link
            to="/payment"
            className="bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-xl"
          >
            Pay Now
          </Link>
        )}
      </div>

      {/* Partners */}
      <div className="mx-4 mt-5 mb-2">
        <h3 className={`font-semibold text-sm mb-2 ${title}`}>Our Partners</h3>
        <div className="overflow-hidden">
          <div className="flex gap-2.5 animate-partner-scroll w-max">
            {sliderList.map((name, i) => (
              <div
                key={i}
                className={`rounded-xl px-4 py-3 text-center min-w-[110px] shrink-0 ${partnerCard}`}
              >
                <p
                  className={`text-xs font-bold whitespace-nowrap ${
                    dark ? 'text-gray-200' : 'text-gray-700'
                  }`}
                >
                  {name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Navigation />
    </div>
  );
}

export default Dashboard;